import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import { NotificationBell } from '@/features/notifications/NotificationBell';
import { Button, ButtonLink } from '@/components/ui/Button';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES, spaceRouteForRole } from '@/config/navigation';
import { cn } from '@/lib/cn';

const publicLinks = [
  // Le logo ramène à l'accueil, mais tout le monde ne le sait pas : un lien
  // nommé coûte une ligne et supprime le doute.
  { label: 'Accueil', to: ROUTES.home },
  { label: 'Louer', to: ROUTES.search },
  { label: 'Acheter', to: ROUTES.listings },
  { label: 'Vendre', to: ROUTES.becomeSeller },
  // Un seul lien pour les deux annuaires : la page de garage porte l'onglet
  // « Lavage auto » en tête. Sept liens dans une barre à 768 px, ça déborde.
  { label: 'Entretien', to: ROUTES.garages },
  { label: 'Comment ça marche', to: ROUTES.howItWorks },
];

export function Header() {
  const { isAuthenticated, role, profile, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Le menu se referme dès que la page change, quelle que soit la façon dont on
  // en est sorti. Chaque lien portait déjà sa propre fermeture — mais pas les
  // BOUTONS (Mon espace, Connexion, Créer un compte), qui laissaient le menu
  // ouvert par-dessus la page qu'on venait de demander. Fermer sur le
  // changement de route couvre tous les cas, y compris ceux qu'on ajoutera.
  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-40 border-b border-ink-200 bg-white/95 backdrop-blur">
      <div className="cf-container flex h-[72px] items-center gap-2.5 md:gap-4">
        <Logo withTagline />

        <nav className="ml-6 hidden items-center gap-6 md:flex">
          {publicLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              // Sans `end`, « / » correspond à TOUTES les routes : le lien
              // Accueil resterait allumé sur chaque page du site.
              end={link.to === ROUTES.home}
              className={({ isActive }) =>
                cn(
                  'text-sm font-semibold text-ink-700 transition-colors hover:text-ink-950',
                  isActive && 'text-ink-950',
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden items-center gap-4 md:flex">
          {isAuthenticated ? (
            <>
              <NotificationBell />
              <ButtonLink to={spaceRouteForRole(role)} variant="secondary" size="sm">
                {profile?.first_name ? `Bonjour, ${profile.first_name}` : 'Mon espace'}
              </ButtonLink>
              <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                Déconnexion
              </Button>
            </>
          ) : (
            <>
              <Link
                to={ROUTES.signIn}
                className="text-sm font-semibold text-ink-950 hover:text-brand-600"
              >
                Connexion
              </Link>
              <ButtonLink to={ROUTES.signUp} size="sm">
                Créer un compte
              </ButtonLink>
            </>
          )}
        </div>

        {/* Sur téléphone la cloche reste dans la barre, à côté du menu : une
            notification qu'il faut aller déplier derrière un burger n'alerte
            plus personne. */}
        {isAuthenticated && <NotificationBell className="ml-auto md:hidden" />}

        <button
          type="button"
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg border border-ink-200 bg-ink-50 text-ink-800 md:hidden',
            !isAuthenticated && 'ml-auto',
          )}
          aria-label="Ouvrir le menu"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor">
            <path d="M4 7h16M4 12h16M4 17h16" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {open && (
        <div className="border-t border-ink-100 bg-white md:hidden">
          <div className="cf-container flex flex-col gap-1 py-3">
            {publicLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-2 flex gap-2">
              {isAuthenticated ? (
                <>
                  <ButtonLink
                    to={spaceRouteForRole(role)}
                    variant="secondary"
                    size="sm"
                    className="flex-1"
                  >
                    Mon espace
                  </ButtonLink>
                  <Button variant="ghost" size="sm" onClick={() => void signOut()}>
                    Déconnexion
                  </Button>
                </>
              ) : (
                <>
                  <ButtonLink to={ROUTES.signIn} variant="secondary" size="sm" className="flex-1">
                    Connexion
                  </ButtonLink>
                  <ButtonLink to={ROUTES.signUp} size="sm" className="flex-1">
                    Créer un compte
                  </ButtonLink>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
